import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Modal,
  Table,
  Badge
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faTrash, faSave } from '@fortawesome/free-solid-svg-icons';
import { useParams, useHistory, Link } from 'react-router-dom';

const GroupForm = () => {
  const { id } = useParams();
  const history = useHistory();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    created_by: ''
  });
  const [members, setMembers] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (isEdit && id) {
      fetchGroupData();
    }
  }, [id, isEdit]);

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/groups/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch group data');
      }

      const data = await response.json();
      const group = data.data;
      
      setFormData({
        name: group.name,
        created_by: group.created_by
      });
      
      // Set members from group data
      if (group.groupMembers) {
        const memberUsers = group.groupMembers.map(member => ({
          id: member.user.id,
          name: member.user.name,
          email: member.user.email,
          role: member.role,
          status: member.status
        }));
        setMembers(memberUsers);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const searchUsers = async () => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/users/search?q=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to search users');
      }

      const data = await response.json();
      setSearchResults(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddMember = (user) => {
    // Check if user is already a member
    if (members.find(member => member.id === user.id)) {
      setError('User is already a member of this group');
      return;
    }

    // Check if we haven't reached the limit
    if (members.length >= 5) {
      setError('Group cannot have more than 5 members');
      return;
    }

    const newMember = {
      id: user.id,
      name: user.name,
      email: user.email,
      role: 'member',
      status: 'active'
    };

    setMembers(prev => [...prev, newMember]);
    setShowSearchModal(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveMember = (userId) => {
    setMembers(prev => prev.filter(member => member.id !== userId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Group name is required');
      return;
    }

    if (members.length === 0) {
      setError('At least one member is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('adminToken');
      const url = isEdit ? `/api/admin/groups/${id}` : '/api/admin/groups';
      const method = isEdit ? 'PUT' : 'POST';
      
      const payload = {
        name: formData.name,
        created_by: parseInt(formData.created_by),
        members: members.map(member => ({
          user_id: member.id,
          role: member.role,
          status: member.status
        }))
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Failed to ${isEdit ? 'update' : 'create'} group`);
      }

      setSuccess(`Group ${isEdit ? 'updated' : 'created'} successfully`);
      
      // Redirect to groups list after a short delay
      setTimeout(() => {
        history.push('/admin/groups');
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/admin/groups">Groups</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {isEdit ? 'Edit Group' : 'Create Group'}
              </li>
            </ol>
          </nav>
          <h2 className="h4">{isEdit ? 'Edit Group' : 'Create New Group'}</h2>
          <p className="mb-0">{isEdit ? 'Update group information and members' : 'Create a new group with members'}</p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          <Card className="border-0 shadow">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Group Information</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Group Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter group name"
                        required
                        maxLength={50}
                      />
                      <Form.Text className="text-muted">
                        {formData.name.length}/50 characters
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Creator ID *</Form.Label>
                      <Form.Control
                        type="number"
                        name="created_by"
                        value={formData.created_by}
                        onChange={handleInputChange}
                        placeholder="Enter creator user ID"
                        required
                        disabled={isEdit}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {isEdit ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {isEdit ? 'Update Group' : 'Create Group'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow">
            <Card.Header className="bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Group Members</h5>
                <Badge bg="secondary">{members.length}/5</Badge>
              </div>
            </Card.Header>
            <Card.Body>
              {members.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No members added yet</p>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowSearchModal(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add Member
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowSearchModal(true)}
                      disabled={members.length >= 5}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      Add Member
                    </Button>
                  </div>
                  
                  <div className="members-list">
                    {members.map((member) => (
                      <div key={member.id} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                        <div>
                          <div className="fw-bold">{member.name}</div>
                          <small className="text-muted">{member.email}</small>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveMember(member.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* User Search Modal */}
      <Modal show={showSearchModal} onHide={() => setShowSearchModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Members</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Control
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value.length >= 2) {
                  searchUsers();
                } else {
                  setSearchResults([]);
                }
              }}
            />
          </div>

          {searchLoading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Searching...</span>
            </div>
          )}

          {searchResults.length > 0 && (
            <Table responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((user) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleAddMember(user)}
                        disabled={members.find(member => member.id === user.id)}
                      >
                        Add
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {searchTerm.length >= 2 && searchResults.length === 0 && !searchLoading && (
            <div className="text-center py-3">
              <p className="text-muted">No users found</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSearchModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GroupForm;
